/**
 * TruePrice V1 - PSI Constants
 * 
 * Price per Square Inch market constants.
 * Source: Addendum D (Empirical Constants), Addendum E (Master Boosts)
 */

import { MediumKey } from '../types/medium';

/**
 * Base PSI values anchored at Medium size (24×30 equivalent)
 * Source: Addendum D Section 2.1
 * 
 * These are "brutal truth" values from empirical research.
 * Units: USD per square inch
 */
export const PSI_BASE: Record<MediumKey, number> = {
  oil: 2.50,           // The King. Holds value at scale.
  acrylic: 1.50,       // Canvas-based. Outsells all paper mediums.
  watercolor: 1.10,    // The Paper Ceiling. Dedicated collector market but paper penalty.
  gouache: 0.95,       // Limited market data. Groups with watercolor, should not exceed it.
  pastel: 1.35,        // The Volatile. Paper-based perception keeps it below acrylic on canvas.
  mixed_media: 1.00,   // The most variable. "Hardest to price, often lowest." Floored conservatively.
};

/**
 * Master PSI overrides for Established+ career stage
 * Source: Addendum E Section 3.5
 * 
 * Paper-based mediums get a boost to support master-level labor.
 * Applied when career_stage >= 'established'
 */
export const MASTER_PSI: Record<MediumKey, number> = {
  oil: 3.50,           // Steepest career-price curve of any medium
  acrylic: 2.00,       // +$0.50 boost
  watercolor: 1.80,    // +$0.70 boost (Paper Ceiling fix)
  gouache: 1.40,       // +$0.45 boost
  pastel: 2.00,        // +$0.65 boost
  mixed_media: 1.60,   // +$0.60 boost
};

/**
 * Get the appropriate PSI base for a medium and career stage
 */
export function getPSIBase(medium: MediumKey, isEstablished: boolean): number {
  if (isEstablished) {
    return MASTER_PSI[medium];
  }
  return PSI_BASE[medium];
}
